import { Atom, useAtomValue } from 'jotai';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { PlaylistEntry } from '@statsbomb/video-client-js';
import { CardBody, CardHeader } from '@statsbomb/kitbag-components';
import { METRIC_KEY } from '@/consts/searchKeys';
import { snakeToCamel } from '@/utils/queries';
import { VideoPlayerHeader } from './VideoPlayerHeader';
import { CloseCardHeaderButton } from '../button/CloseCardHeaderButton';
import { VideoPlayer } from './VideoPlayer';

export const VideoPlayerPanelHeaderBase = ({
  title,
  onCloseNavigationPath = '..',
  onCloseParamsToDelete = [],
  isLoading = false,
}: {
  title?: string;
  onCloseNavigationPath?: string;
  onCloseParamsToDelete?: string[];
  isLoading?: boolean;
}) => {
  const { t } = useTranslation('general');
  const headerTitle = isLoading ? t('loading') : title;

  return (
    <CardHeader>
      <VideoPlayerHeader title={headerTitle} allowFullScreen={false} />
      <CloseCardHeaderButton navigationPath={onCloseNavigationPath} paramsToDelete={onCloseParamsToDelete} />
    </CardHeader>
  );
};

export const VideoPlayerPanelBodyBase = ({
  videoEvents = [],
  unavailableVideosCount,
  isLoading = false,
}: {
  videoEvents?: PlaylistEntry[];
  unavailableVideosCount?: number;
  isLoading?: boolean;
}) => {
  const { t } = useTranslation('status');
  const statusProps = isLoading
    ? {
        statusType: 'loading',
        statusTitle: t('general.loading.title'),
        statusDescription: t('general.loading.description'),
      }
    : {};

  return (
    <CardBody {...statusProps}>
      <VideoPlayer videoEvents={videoEvents} unavailableVideosCount={unavailableVideosCount} />
    </CardBody>
  );
};

export const EntityVideoPlayerPanelHeader = ({
  entityNameAtom,
  entityIdParam,
}: {
  entityNameAtom: Atom<Promise<string> | string>;
  entityIdParam: string;
}) => {
  const { t } = useTranslation('metrics');
  const [searchParams] = useSearchParams();
  const metricKeyParam = searchParams.get(METRIC_KEY);
  const metricTranslationKey = t(`${snakeToCamel(metricKeyParam)}.name`);
  const entityName = useAtomValue(entityNameAtom);
  const title = `${entityName} - ${metricTranslationKey}`;
  const isLoading = !entityName || !metricTranslationKey;

  return (
    <VideoPlayerPanelHeaderBase
      title={title}
      onCloseParamsToDelete={[entityIdParam, METRIC_KEY]}
      isLoading={isLoading}
    />
  );
};
